import { formatDistance } from "date-fns";
import { ForwardedRef, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  Avatar,
  Body,
  Ellipsis,
  onEnterKey,
  useToast,
} from "@songtradr-one/components-frontend";
import {
  NotificationStatus,
  unmaskFragment,
} from "@songtradr-one/shared-common";
import { Flex, T, useAppContext } from "@songtradr-one/shared-frontend";
import { notificationActions } from "../notification-actions/NotificationActions.Parts";
import {
  Content,
  DEFAULT_SUBJECT_TEXT,
  NotificationItem_MarkAsRead,
  NotificationItem_NotificationFragment,
  Props,
  TimeText,
  UnreadDot,
  Wrapper,
  getTemplateProps,
} from "./NotificationItem.Parts";

export const NotificationItem = forwardRef(
  ({ notification, onNavigate }: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { addToast } = useToast();
    const { user } = useAppContext();
    const client = useApolloClient();
    const navigate = useNavigate();
    const [markAsReadMutation] = useMutation(NotificationItem_MarkAsRead);

    const item = unmaskFragment(
      NotificationItem_NotificationFragment,
      notification,
    );
    const { id, status, date, dest, subject, key, data } = item;

    const action = notificationActions[key];
    const { primary, secondary, vars } = getTemplateProps(item);
    const read = status === NotificationStatus.Read;
    const clickable = Boolean(dest) || Boolean(action) || !read;

    const goTo = (to: string) => {
      onNavigate?.();
      navigate(to);
    };

    const handleClick = async () => {
      if (!read)
        await markAsReadMutation({
          variables: { id },
          optimisticResponse: { markNotificationAsRead: true },
          update: (cache) => {
            cache.modify({
              id: cache.identify({ __typename: "Notification", id }),
              fields: { status: () => NotificationStatus.Read },
            });
            cache.modify({
              fields: {
                notifications: (existing, { isReference }) =>
                  isReference(existing)
                    ? existing
                    : {
                        ...existing,
                        unreadCount: Math.max(
                          0,
                          ((existing as { unreadCount?: number }).unreadCount ??
                            0) - 1,
                        ),
                      },
              },
            });
          },
        });

      // Action notifications (e.g. bulk download) run in place; navigational
      // ones fall back to their dest.
      if (action) {
        const actionData = Object.fromEntries(
          (data ?? []).map((entry) => [entry.key, entry.value]),
        );
        await action({ client, addToast, data: actionData, navigate: goTo });
        return;
      }

      // dest is normally an in-app route; fall back to a hard navigation
      // for the rare case it's an absolute, cross-origin URL.
      if (dest) {
        onNavigate?.();
        if (dest.startsWith("http")) window.location.href = dest;
        else navigate(dest);
      }
    };

    return (
      <Wrapper
        ref={ref}
        tabIndex={clickable ? 0 : -1}
        $clickable={clickable}
        onClick={clickable ? handleClick : undefined}
        onKeyDown={clickable ? onEnterKey(handleClick) : undefined}
      >
        <Avatar
          height="2rem"
          width="2rem"
          img={subject?.avatar ?? user?.picture}
        />
        <Content>
          <Flex $align="center" $gap="0.25rem">
            <Flex $gap="0.25rem" $flex="1" $minWidth="0">
              <Body variant="normal-strong">
                {subject?.name ?? DEFAULT_SUBJECT_TEXT}
              </Body>
              <T template={primary} vars={vars}>
                {(c) => (
                  <Ellipsis>
                    <Body variant="normal">{c}</Body>
                  </Ellipsis>
                )}
              </T>
            </Flex>
            {!read && <UnreadDot />}
          </Flex>
          {secondary && (
            <T template={secondary} vars={vars}>
              {(c) => (
                <Ellipsis>
                  <Body variant="small">{c}</Body>
                </Ellipsis>
              )}
            </T>
          )}
          <TimeText variant="xsmall">
            {formatDistance(new Date(date), new Date(), { addSuffix: true })}
          </TimeText>
        </Content>
      </Wrapper>
    );
  },
);
